import { z } from 'zod';
import { ExpenseTypeSchema } from '../enums';

// Base Expense Schema
export const BaseExpenseSchema = z.object({
  expenseId: z.string().optional(), // MongoDB ObjectId
  expenseType: ExpenseTypeSchema,
  description: z.string().min(1, 'Description is required').max(500, 'Description too long'),
  amount: z.number().positive('Amount must be positive').max(10000000, 'Amount too large'),
  accountId: z.string(), // Reference to AccountID
  givenBy: z.string().min(1, 'Given by is required').max(100, 'Name too long'),
  time: z.date().optional(), // Server time when recorded
  deviceId: z.string().min(1, 'Device ID is required'),
  officeNo: z.number().int().positive('Office number must be positive'),
  regionId: z.number().int().positive('Region ID must be positive'),
  slipNo: z.string().optional(),
  remarks: z.string().max(500, 'Remarks too long').optional(),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional()
});

export type BaseExpense = z.infer<typeof BaseExpenseSchema>;

// Expense Creation Schema
export const CreateExpenseSchema = BaseExpenseSchema.omit({
  expenseId: true,
  time: true,
  createdAt: true,
  updatedAt: true
});

export type CreateExpense = z.infer<typeof CreateExpenseSchema>;

// Expense Update Schema
export const UpdateExpenseSchema = BaseExpenseSchema.partial().extend({
  expenseId: z.string() // Required for updates
});

export type UpdateExpense = z.infer<typeof UpdateExpenseSchema>;

// Expense Response Schema (for API responses)
export const ExpenseResponseSchema = BaseExpenseSchema.extend({
  expenseId: z.string()
});

export type ExpenseResponse = z.infer<typeof ExpenseResponseSchema>;

// Expense with Relations Schema
export const ExpenseWithRelationsSchema = ExpenseResponseSchema.extend({
  account: z.object({
    accountId: z.string(),
    accountName: z.string(),
    accountNumber: z.string()
  }).optional(),
  office: z.object({
    officeNo: z.number(),
    officeName: z.string(),
    regionId: z.number(),
    regionName: z.string()
  }).optional(),
  recordedByUser: z.object({
    userId: z.string(),
    userName: z.string(),
    userRole: z.string()
  }).optional()
});

export type ExpenseWithRelations = z.infer<typeof ExpenseWithRelationsSchema>;

// Expense List Query Schema
export const ExpenseListQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  officeNo: z.number().int().positive().optional(),
  regionId: z.number().int().positive().optional(),
  expenseType: ExpenseTypeSchema.optional(),
  accountId: z.string().optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  minAmount: z.number().positive().optional(),
  maxAmount: z.number().positive().optional(),
  givenBy: z.string().optional(),
  sortBy: z.enum(['time', 'amount', 'expenseType', 'createdAt']).default('time'),
  sortOrder: z.enum(['asc', 'desc']).default('desc')
});

export type ExpenseListQuery = z.infer<typeof ExpenseListQuerySchema>;

// Expense Statistics Schema
export const ExpenseStatisticsSchema = z.object({
  totalExpenses: z.number(),
  totalAmount: z.number(),
  byType: z.array(z.object({
    type: ExpenseTypeSchema,
    count: z.number(),
    amount: z.number()
  })),
  byAccount: z.array(z.object({
    accountId: z.string(),
    accountName: z.string(),
    count: z.number(),
    amount: z.number()
  })),
  byOffice: z.array(z.object({
    officeNo: z.number(),
    officeName: z.string(),
    count: z.number(),
    amount: z.number()
  })),
  period: z.object({
    start: z.date(),
    end: z.date()
  })
});

export type ExpenseStatistics = z.infer<typeof ExpenseStatisticsSchema>;

// Expense Receipt Schema
export const ExpenseReceiptSchema = z.object({
  expenseId: z.string(),
  expenseType: ExpenseTypeSchema,
  description: z.string(),
  amount: z.number(),
  accountName: z.string(),
  givenBy: z.string(),
  date: z.date(),
  officeName: z.string(),
  slipNo: z.string().optional()
});

export type ExpenseReceipt = z.infer<typeof ExpenseReceiptSchema>; 